import { Box, Button, Divider, Grid, Typography } from '@mui/material';
import { FC, useState } from 'react';
import SubCard from 'ui-component/cards/SubCard';
import Chart from 'Application/components/CustomChart';
import { AppTabsContainer, BodyText, SubTitleText } from 'Application/components';
import CreditLimitDialog from 'Application/components/Modals/SetCredit';
import EmptyStateContainer from 'Application/components/EmptyState';
import { AppTab } from 'Application/models';
import AllocationList from './CreditReport/Allocation';
import { string } from 'yup';
import UsageList from './CreditReport/Usage';

interface BritamAgencyAdminCreditProps {
    currentAmount: number;
    remainingAmount: number;
    name: string;
    currency: string;
}

export const BritamAgencyAdminCredit: FC<BritamAgencyAdminCreditProps> = ({ currentAmount, remainingAmount, name, currency }) => {
    const [isSetCreditOpen, setIsCreditLimitDialogOpen] = useState(false);

    const setCreditDialog = () => {
        setIsCreditLimitDialogOpen(true);
    };

    const appTabsList: AppTab[] = [
        {
            index: 0,
            label: 'Credit Allocation Report',
            content: <AllocationList agencyName={name} currency={currency} />
        },
        {
            index: 1,
            label: 'Credit Usage Report',
            content: <UsageList agencyName={name} currency={currency} />
        }
    ];

    return (
        <>
            <Box>
                <Box className="flex justify-between items-center">
                    <Box className="flex flex-col space-y-2">
                        <SubTitleText text={'Agency Credits'} />
                        <BodyText text={'Explore the credit usage by the agency'} />
                    </Box>
                    <Button disableElevation onClick={setCreditDialog} variant="contained">
                        Edit
                    </Button>
                </Box>
                <Divider className="my-6" />
                <Grid container>
                    <Grid item xs={12} sm={4}>
                        <Typography variant="subtitle1">Account Credits</Typography>
                        <Typography variant="subtitle2">User Credit Usage</Typography>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        <SubCard>
                            {!currentAmount && currentAmount !== 0 ? (
                                <EmptyStateContainer
                                    title="Credit limit not set"
                                    action="Click on the edit button to set agency credit limit"
                                />
                            ) : (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'flex-start',
                                        alignItems: 'flex-start'
                                    }}
                                >
                                    <Grid container spacing={2}>
                                        {/* Chart section */}
                                        <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                            <Chart currentAmount={currentAmount} remainingAmount={remainingAmount} />
                                        </Grid>

                                        {/* Information section */}
                                        <Grid item xs={12} sm={8}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    justifyContent: 'flex-start',
                                                    alignItems: 'flex-start',
                                                    width: '100%'
                                                }}
                                            >
                                                <Typography variant="subtitle1">
                                                    Initial Credit Limit = {currency} {currentAmount}
                                                </Typography>
                                                <Box pt={4}>
                                                    <Typography variant="subtitle2">Current Balance</Typography>
                                                    <Typography variant="h3">
                                                        {currency} {remainingAmount}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}
                        </SubCard>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Typography variant="subtitle1">Credits Reports</Typography>
                        <Typography variant="subtitle2">Track usage of the Agency Credit Limit</Typography>
                    </Grid>
                </Grid>
            </Box>
            <CreditLimitDialog
                isOpen={isSetCreditOpen}
                setIsOpen={setIsCreditLimitDialogOpen}
                remainingAmount={remainingAmount}
                currency={currency}
            />

            <Grid item xs={12} sm={4} sx={{ pt: 2 }}>
                <AppTabsContainer appTabsList={appTabsList} />
            </Grid>
        </>
    );
};
